import { List, Map } from 'immutable'

/**
 * Aggregate shape of the Redux store slices read by the Community service.
 *
 * Every slice is `Map<string, unknown>` because that is Immutable.js's
 * genuine contract: the values inside each Map are heterogeneous, and the
 * typed selectors below are responsible for narrowing them at the boundary.
 *
 * Consumers usually need only one slice — each selector below accepts the
 * minimum subset (e.g. `{ auth: Map<string, unknown> }`) so the helpers
 * compose with the structural subtyping the existing code already uses.
 */
export interface CommunityReduxState {
  auth: Map<string, unknown>
  communityProfile: Map<string, unknown>
  user: Map<string, unknown>
  userProfiles: Map<string, unknown>
}

const EMPTY_MAP: Map<string, unknown> = Map<string, unknown>()

/**
 * Single point where we promise the runtime that an Immutable Map has
 * string keys. Immutable's own `Map.isMap` only narrows to
 * `Map<unknown, unknown>`, so every consumer funnels through this
 * predicate to keep the inevitable cast in one place.
 */
export function isStringMap (value: unknown): value is Map<string, unknown> {
  return Map.isMap(value)
}

// ── Auth ─────────────────────────────────────────────────────────────

export function selectAuthData (
  state: { auth: Map<string, unknown> },
): Map<string, unknown> | undefined {
  return isStringMap(state.auth) ? state.auth : undefined
}

export function selectAuthUid (
  state: { auth: Map<string, unknown> },
): string | undefined {
  if (!isStringMap(state.auth)) return undefined
  const uid = state.auth.get('uid')
  if (typeof uid === 'string') return uid
  if (typeof uid === 'number') return String(uid)
  return undefined
}

export function selectAuthJwt (
  state: { auth: Map<string, unknown> },
): string {
  if (!isStringMap(state.auth)) return ''
  const jwt = state.auth.get('jwt')
  return typeof jwt === 'string' ? jwt : ''
}

export function selectAuthUsername (
  state: { auth: Map<string, unknown> },
): string {
  if (!isStringMap(state.auth)) return ''
  const username = state.auth.get('username')
  return typeof username === 'string' ? username : ''
}

// ── Community profile slice ──────────────────────────────────────────

export function selectCommunityProfile (
  state: { communityProfile: Map<string, unknown> },
): Map<string, unknown> | undefined {
  return isStringMap(state.communityProfile) ? state.communityProfile : undefined
}

export function selectProfilePicturesData (
  state: { communityProfile: Map<string, unknown> },
): Map<string, unknown> {
  if (!isStringMap(state.communityProfile)) return EMPTY_MAP
  const data = state.communityProfile.getIn(['profilePictures', 'data'])
  return isStringMap(data) ? data : EMPTY_MAP
}

export function selectProfilePictureForUid (
  state: { communityProfile: Map<string, unknown> },
  uid: string,
): Map<string, unknown> | undefined {
  if (!isStringMap(state.communityProfile)) return undefined
  const value = state.communityProfile.getIn(['profilePictures', 'data', uid])
  return isStringMap(value) ? value : undefined
}

// ── User profiles (multi-profile list) slice ────────────────────────

export function selectUserProfilesDataMap (
  state: { userProfiles: Map<string, unknown> },
): Map<string, unknown> | undefined {
  if (!isStringMap(state.userProfiles)) return undefined
  const data = state.userProfiles.get('data')
  return isStringMap(data) ? data : undefined
}

// ── User slice ──────────────────────────────────────────────────────

export function selectUserData (
  state: { user: Map<string, unknown> },
): Map<string, unknown> | undefined {
  return isStringMap(state.user) ? state.user : undefined
}

export function selectUserLanguage (
  state: { user: Map<string, unknown> },
): List<string> | string | undefined {
  if (!isStringMap(state.user)) return undefined
  const value = state.user.getIn(['data', 'language'])
  if (typeof value === 'string') return value
  // List.isList only narrows to List<unknown>; the schema guarantees strings.
  if (List.isList(value)) return value as List<string>
  return undefined
}
